'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { ScrollReveal } from '@/components/PageTransition';
import { ObservationChainTapestry } from '@/app/open-letter/components/Plot3MuralEnhancements';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface ArchiveChain {
  id: string;
  title: string;
  arabic_title?: string;
  description?: string;
  city?: string;
  domain?: string;
  theme?: string;
  name_of_allah?: string;
  observation_count: number;
  contributor_count: number;
  contributors: { name: string; role: string; badge?: string }[];
  created_at: string;
  updated_at?: string;
  is_shared: boolean;
  tags?: string[];
  impact_score?: number;
}

interface ArchiveFilters {
  search: string;
  city: string;
  domain: string;
  theme: string;
  sort: 'recent' | 'observations' | 'contributors' | 'impact';
  nameOfAllah: string;
}

// ─── STATIC ARCHIVE DATA ─────────────────────────────────────────────────────

const ARCHIVE_CHAINS: ArchiveChain[] = [
  {
    id: 'ac1',
    title: 'The Leaf Sequence — Al-Qadir to An-Nur',
    arabic_title: 'تسلسل الورقة',
    description: 'A 6-observation chain tracing the Names of Allah through the lifecycle of a single leaf — from the decree of Al-Qadir through the light of An-Nur. The foundational Observation Chain of the Haal Project.',
    city: 'Chicago',
    domain: 'Ecology',
    theme: 'Leaf Sequence',
    name_of_allah: 'Al-Qadir → An-Nur',
    observation_count: 6,
    contributor_count: 12,
    contributors: [
      { name: 'Sister Amira', role: 'inner-seeker', badge: 'Leaf Keeper' },
      { name: 'Dr. Yusuf', role: 'academic', badge: 'Scholar' },
      { name: 'Brother Tariq', role: 'inner-seeker', badge: 'Observer' },
    ],
    created_at: '2026-01-15T10:00:00Z',
    updated_at: '2026-03-01T14:00:00Z',
    is_shared: true,
    tags: ['leaf', 'sequence', 'ecology', 'names', 'creation'],
    impact_score: 94,
  },
  {
    id: 'ac2',
    title: 'Mizan in the Streets — Justice Observation Chain',
    arabic_title: 'الميزان في الشوارع',
    description: 'A 9-observation chain documenting the presence and absence of Mizan (balance) in Atlanta\'s Vine City neighborhood — from cracked sidewalks to community gardens, each observation linked to Al-Adl and Al-Haqq.',
    city: 'Atlanta',
    domain: 'Justice',
    theme: 'Urban Justice',
    name_of_allah: 'Al-Adl',
    observation_count: 9,
    contributor_count: 5,
    contributors: [
      { name: 'Ustadha Khadija', role: 'activist', badge: 'Justice Advocate' },
      { name: 'Brother Khalid', role: 'activist', badge: 'Community Mapper' },
    ],
    created_at: '2026-01-28T09:00:00Z',
    updated_at: '2026-02-15T11:00:00Z',
    is_shared: true,
    tags: ['justice', 'mizan', 'urban', 'community', 'al-adl'],
    impact_score: 87,
  },
  {
    id: 'ac3',
    title: 'Mycorrhizal Network — Underground Amanah',
    arabic_title: 'شبكة الفطريات',
    description: 'A 7-observation chain exploring the mycorrhizal fungal networks beneath Portland\'s Forest Park as a model of Amanah — the hidden infrastructure of trust that sustains visible life. Linked to Al-Latif (the Subtle) and Al-Khabir (the Aware).',
    city: 'Portland',
    domain: 'Ecology',
    theme: 'Mycorrhizal Network',
    name_of_allah: 'Al-Latif',
    observation_count: 7,
    contributor_count: 3,
    contributors: [
      { name: 'Dr. Amina', role: 'academic', badge: 'Ecological Scholar' },
    ],
    created_at: '2026-02-03T08:00:00Z',
    is_shared: true,
    tags: ['ecology', 'mycorrhizal', 'amanah', 'al-latif', 'forest'],
    impact_score: 76,
  },
  {
    id: 'ac4',
    title: '40 Days Fisabilillah — Detroit Service Chain',
    arabic_title: 'أربعون يوماً في سبيل الله',
    description: 'A 40-observation chain linking consecutive daily acts of service in Detroit — from feeding the homeless to visiting the sick to tutoring youth. Each observation tagged with a Name of Allah and an Ayat reference. Shared as a garment.',
    city: 'Detroit',
    domain: 'Service',
    theme: 'Fisabilillah',
    name_of_allah: 'Al-Wadud',
    observation_count: 40,
    contributor_count: 8,
    contributors: [
      { name: 'Collective', role: 'community-caretaker', badge: 'Chain Weaver' },
      { name: 'Ustadh Yusuf', role: 'community-caretaker', badge: 'Amanah Holder' },
    ],
    created_at: '2026-02-10T07:00:00Z',
    updated_at: '2026-03-10T20:00:00Z',
    is_shared: true,
    tags: ['fisabilillah', 'service', 'detroit', 'daily', 'garment'],
    impact_score: 98,
  },
  {
    id: 'ac5',
    title: 'Reentry as Islah — Observation Chain',
    arabic_title: 'الإصلاح كعودة',
    description: 'A 5-observation chain documenting the reentry journey of three formerly incarcerated brothers in Atlanta, framing each stage — release, housing, employment, community, purpose — through the lens of Islah (restoration) and Al-Tawwab (the Acceptor of Repentance).',
    city: 'Atlanta',
    domain: 'Justice & Reentry',
    theme: 'Islah',
    name_of_allah: 'Al-Tawwab',
    observation_count: 5,
    contributor_count: 4,
    contributors: [
      { name: 'Brother Khalid', role: 'activist', badge: 'Justice Advocate' },
    ],
    created_at: '2026-02-18T10:00:00Z',
    is_shared: true,
    tags: ['reentry', 'islah', 'justice', 'al-tawwab', 'restoration'],
    impact_score: 82,
  },
  {
    id: 'ac6',
    title: 'Urban Ayat — Chicago South Side Mapping',
    arabic_title: 'آيات المدينة',
    description: 'A 12-observation chain mapping Ayat (signs) in the urban landscape of Chicago\'s South Side — from the geometry of a cracked sidewalk to the pattern of a community mural. Each observation linked to a Name of Allah and a Quranic verse.',
    city: 'Chicago',
    domain: 'Ecology',
    theme: 'Urban Ayat',
    name_of_allah: 'Al-Zahir',
    observation_count: 12,
    contributor_count: 7,
    contributors: [
      { name: 'Sister Fatima', role: 'community-caretaker', badge: 'Urban Observer' },
      { name: 'Teacher Maryam', role: 'academic', badge: 'Educator' },
    ],
    created_at: '2026-02-25T09:00:00Z',
    is_shared: true,
    tags: ['urban', 'ayat', 'chicago', 'signs', 'al-zahir'],
    impact_score: 79,
  },
  {
    id: 'ac7',
    title: 'Water Cycle as Tawbah — Portland Rain Chain',
    arabic_title: 'دورة المياه كتوبة',
    description: 'A 6-observation chain tracing the water cycle in Portland\'s watershed as a model of Tawbah (return) — evaporation as departure, clouds as gathering, rain as descent, rivers as return. Linked to Al-Ghaffar and Al-Tawwab.',
    city: 'Portland',
    domain: 'Ecology',
    theme: 'Water Cycle',
    name_of_allah: 'Al-Ghaffar',
    observation_count: 6,
    contributor_count: 2,
    contributors: [
      { name: 'Dr. Amina', role: 'academic', badge: 'Ecological Scholar' },
    ],
    created_at: '2026-03-01T08:00:00Z',
    is_shared: true,
    tags: ['water', 'tawbah', 'ecology', 'portland', 'al-ghaffar'],
    impact_score: 71,
  },
  {
    id: 'ac8',
    title: 'Masjid as Masjid — Detroit Sacred Space Chain',
    arabic_title: 'المسجد كمسجد',
    description: 'A 8-observation chain documenting the sacred geography of Detroit\'s east side — every corner, park, and gathering place as a potential Masjid. Rooted in the hadith "the whole earth has been made a Masjid for me."',
    city: 'Detroit',
    domain: 'Community',
    theme: 'Sacred Space',
    name_of_allah: 'Al-Quddus',
    observation_count: 8,
    contributor_count: 6,
    contributors: [
      { name: 'Imam Tariq', role: 'community-caretaker', badge: 'Sacred Space Keeper' },
      { name: 'Ustadh Yusuf', role: 'community-caretaker', badge: 'Amanah Holder' },
    ],
    created_at: '2026-03-05T10:00:00Z',
    is_shared: true,
    tags: ['masjid', 'sacred', 'detroit', 'community', 'al-quddus'],
    impact_score: 85,
  },
];

const CITY_OPTIONS = ['All Cities', 'Chicago', 'Atlanta', 'Portland', 'Detroit'];
const DOMAIN_OPTIONS = ['All Domains', 'Ecology', 'Justice', 'Service', 'Justice & Reentry', 'Community', 'Education', 'Health'];
const THEME_OPTIONS = ['All Themes', 'Leaf Sequence', 'Urban Justice', 'Mycorrhizal Network', 'Fisabilillah', 'Islah', 'Urban Ayat', 'Water Cycle', 'Sacred Space'];
const NAME_OPTIONS = ['All Names', 'Al-Qadir', 'Al-Adl', 'Al-Latif', 'Al-Wadud', 'Al-Tawwab', 'Al-Zahir', 'Al-Ghaffar', 'Al-Quddus'];

const SORT_OPTIONS = [
  { id: 'recent', label: '↓ Recent' },
  { id: 'observations', label: '◎ Observations' },
  { id: 'contributors', label: '⬡ Contributors' },
  { id: 'impact', label: '✦ Impact' },
];

const ROLE_COLORS: Record<string, string> = {
  'inner-seeker': '#8a8ab8',
  'community-caretaker': '#2d7a47',
  'activist': '#2d8a87',
  'academic': '#D4A82A',
};

const DOMAIN_COLORS: Record<string, string> = {
  'Ecology': '#2d7a47',
  'Justice': '#c05050',
  'Service': '#2d8a87',
  'Justice & Reentry': '#b47828',
  'Community': '#2d8a87',
  'Education': '#D4A82A',
  'Health': '#5064b4',
};

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function ChainCard({ chain, index = 0 }: { chain: ArchiveChain; index?: number }) {
  const domainColor = DOMAIN_COLORS[chain.domain || ''] || '#c9a84c';

  return (
    <ScrollReveal delay={Math.min(index * 0.08, 0.4)} direction="up">
      <div style={{
        background: 'rgba(6,13,18,0.7)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderTop: `3px solid ${domainColor}`,
        borderRadius: 8,
        padding: '20px',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        height: '100%',
      }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 24px ${domainColor}18`; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            {chain.arabic_title && (
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${domainColor}70`, direction: 'rtl', marginBottom: 4 }}>
                {chain.arabic_title}
              </p>
            )}
            <h3 style={{ color: '#F5F0E8', fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>
              {chain.title}
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace' }}>
              {timeAgo(chain.updated_at || chain.created_at)}
            </span>
            {chain.impact_score && (
              <span style={{
                fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em',
                padding: '2px 7px', borderRadius: 10,
                background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c',
              }}>
                ✦ {chain.impact_score}
              </span>
            )}
          </div>
        </div>

        {/* Meta badges */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
          {chain.city && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 20,
              background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', color: '#2d7a47',
            }}>
              📍 {chain.city}
            </span>
          )}
          {chain.domain && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 20,
              background: `${domainColor}10`, border: `1px solid ${domainColor}30`, color: domainColor,
            }}>
              {chain.domain}
            </span>
          )}
          {chain.theme && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 20,
              background: 'rgba(138,138,184,0.08)', border: '1px solid rgba(138,138,184,0.2)', color: '#8a8ab8',
            }}>
              {chain.theme}
            </span>
          )}
          {chain.name_of_allah && (
            <span style={{
              fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em',
              padding: '2px 8px', borderRadius: 20,
              background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c',
            }}>
              ✦ {chain.name_of_allah}
            </span>
          )}
        </div>

        {/* Description */}
        {chain.description && (
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7, marginBottom: 14 }}>
            {chain.description}
          </p>
        )}

        {/* Stats row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: domainColor }}>
              {chain.observation_count}
            </span>
            <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              observations
            </span>
          </div>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: '#2d8a87' }}>
              {chain.contributor_count}
            </span>
            <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              contributors
            </span>
          </div>
        </div>

        {/* Contributors */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {chain.contributors.slice(0, 3).map((c, i) => {
            const rc = ROLE_COLORS[c.role] || '#c9a84c';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: `${rc}20`, border: `1px solid ${rc}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, color: rc, fontFamily: 'monospace',
                }}>
                  {c.name[0]}
                </div>
                <div>
                  <span style={{ fontSize: 9, color: '#d4c5a9', fontFamily: 'monospace' }}>{c.name}</span>
                  {c.badge && (
                    <span style={{
                      marginLeft: 4, fontSize: 7, fontFamily: 'monospace', letterSpacing: '.04em', textTransform: 'uppercase',
                      padding: '1px 4px', borderRadius: 8,
                      background: `${rc}10`, border: `1px solid ${rc}25`, color: rc,
                    }}>
                      {c.badge}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {chain.contributor_count > 3 && (
            <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace' }}>
              +{chain.contributor_count - 3} more
            </span>
          )}
        </div>

        {/* Tags */}
        {chain.tags && chain.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
            {chain.tags.slice(0, 5).map(tag => (
              <span key={tag} style={{
                fontSize: 7, fontFamily: 'monospace', letterSpacing: '.04em',
                padding: '1px 6px', borderRadius: 3,
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                color: 'rgba(212,197,169,0.3)',
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Action */}
        <div style={{ display: 'flex', gap: 8 }}>
          <Link href="/inner-reality-lens" style={{
            fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '6px 14px', borderRadius: 4,
            border: `1px solid ${domainColor}40`, color: domainColor, textDecoration: 'none',
            transition: 'all 0.15s',
          }}>
            View Chain →
          </Link>
          <Link href="/inner-reality-lens" style={{
            fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '6px 14px', borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', textDecoration: 'none',
          }}>
            + Contribute
          </Link>
        </div>
      </div>
    </ScrollReveal>
  );
}

function ArchiveSkeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{
          background: 'rgba(6,13,18,0.5)',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: 8, padding: '20px',
        }}>
          <div className="animate-pulse" style={{ width: '80%', height: 18, background: 'rgba(212,197,169,0.06)', borderRadius: 4, marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[1, 2, 3].map(j => (
              <div key={j} className="animate-pulse" style={{ width: 55, height: 16, background: 'rgba(212,197,169,0.05)', borderRadius: 20 }} />
            ))}
          </div>
          <div className="animate-pulse" style={{ width: '100%', height: 13, background: 'rgba(212,197,169,0.04)', borderRadius: 4, marginBottom: 4 }} />
          <div className="animate-pulse" style={{ width: '75%', height: 13, background: 'rgba(212,197,169,0.04)', borderRadius: 4, marginBottom: 14 }} />
          <div style={{ display: 'flex', gap: 16 }}>
            <div className="animate-pulse" style={{ width: 60, height: 24, background: 'rgba(212,197,169,0.05)', borderRadius: 4 }} />
            <div className="animate-pulse" style={{ width: 60, height: 24, background: 'rgba(212,197,169,0.05)', borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ObservationChainArchivePage() {
  const [filters, setFilters] = useState<ArchiveFilters>({
    search: '', city: 'All Cities', domain: 'All Domains',
    theme: 'All Themes', sort: 'recent', nameOfAllah: 'All Names',
  });
  const [loading, setLoading] = useState(true);
  const [liveChains, setLiveChains] = useState<ArchiveChain[]>([]);
  const supabaseRef = useRef(createClient());

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Try to load live chains from Supabase
  useEffect(() => {
    async function loadLive() {
      try {
        const { data } = await supabaseRef.current
          .from('irl_observations')
          .select('id, zahir_text, name_of_allah, created_at, chain_id')
          .not('chain_id', 'is', null)
          .order('created_at', { ascending: false })
          .limit(10);
        // Group by chain_id for live data
        if (data && data.length > 0) {
          const chainMap = new Map<string, any[]>();
          data.forEach((obs: any) => {
            if (obs.chain_id) {
              if (!chainMap.has(obs.chain_id)) chainMap.set(obs.chain_id, []);
              chainMap.get(obs.chain_id)!.push(obs);
            }
          });
          const mapped: ArchiveChain[] = Array.from(chainMap.entries()).map(([chainId, obs]) => ({
            id: `live-${chainId}`,
            title: `Observation Chain — ${obs[0]?.zahir_text?.slice(0, 40) || 'Community Chain'}`,
            description: `A live observation chain with ${obs.length} linked observations from the community.`,
            domain: 'Community',
            observation_count: obs.length,
            contributor_count: 1,
            contributors: [{ name: 'Community Caretaker', role: 'community-caretaker' }],
            created_at: obs[obs.length - 1]?.created_at || new Date().toISOString(),
            is_shared: true,
            name_of_allah: obs[0]?.name_of_allah,
            tags: ['live', 'community'],
          }));
          setLiveChains(mapped);
        }
      } catch { /* silent */ }
    }
    loadLive();
  }, []);

  const allChains = [...ARCHIVE_CHAINS, ...liveChains];

  const filtered = allChains.filter(chain => {
    const q = filters.search.toLowerCase();
    if (q) {
      const matchesSearch =
        chain.title.toLowerCase().includes(q) ||
        chain.description?.toLowerCase().includes(q) ||
        chain.theme?.toLowerCase().includes(q) ||
        chain.name_of_allah?.toLowerCase().includes(q) ||
        chain.tags?.some(t => t.includes(q));
      if (!matchesSearch) return false;
    }
    if (filters.city !== 'All Cities' && chain.city !== filters.city) return false;
    if (filters.domain !== 'All Domains' && chain.domain !== filters.domain) return false;
    if (filters.theme !== 'All Themes' && chain.theme !== filters.theme) return false;
    if (filters.nameOfAllah !== 'All Names' && !chain.name_of_allah?.includes(filters.nameOfAllah)) return false;
    return true;
  }).sort((a, b) => {
    if (filters.sort === 'recent') return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    if (filters.sort === 'observations') return b.observation_count - a.observation_count;
    if (filters.sort === 'contributors') return b.contributor_count - a.contributor_count;
    if (filters.sort === 'impact') return (b.impact_score || 0) - (a.impact_score || 0);
    return 0;
  });

  const activeFiltersCount = [
    filters.city !== 'All Cities',
    filters.domain !== 'All Domains',
    filters.theme !== 'All Themes',
    filters.nameOfAllah !== 'All Names',
  ].filter(Boolean).length;

  function clearFilters() {
    setFilters(f => ({ ...f, city: 'All Cities', domain: 'All Domains', theme: 'All Themes', nameOfAllah: 'All Names', search: '' }));
  }

  return (
    <div style={{ background: '#060d12', minHeight: '100vh', color: '#F5F0E8' }}>
      <Header />

      {/* Hero */}
      <section style={{ padding: '80px 24px 48px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.5)', marginBottom: 12 }}>
            أرشيف سلاسل الملاحظة · OBSERVATION CHAIN ARCHIVE
          </p>
          <h1 style={{ fontFamily: 'monospace', fontSize: 'clamp(16px,3vw,26px)', letterSpacing: '.2em', textTransform: 'uppercase', color: '#F5F0E8', marginBottom: 16 }}>
            OBSERVATION CHAINS
          </h1>
          <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 15, fontStyle: 'italic', lineHeight: 1.7, marginBottom: 12 }}>
            A searchable archive of shared Observation Chains — narrative sequences linking IRL observations into garments of recognition. Organized by city, caretaking domain, and theme.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 13, lineHeight: 1.7, marginBottom: 12, fontStyle: 'italic' }}>
            Each observation here is a recognition — not a discovery. What was recognized was always present, decreed before the caretaker arrived. The Archive grows not because new information enters creation, but because more caretakers are reading what was always written.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 11, fontFamily: 'monospace' }}>
            {ARCHIVE_CHAINS.length + liveChains.length} chains · {ARCHIVE_CHAINS.reduce((s, c) => s + c.observation_count, 0)} recognitions · {ARCHIVE_CHAINS.reduce((s, c) => s + c.contributor_count, 0)} contributors
          </p>
        </div>
      </section>

      {/* ── TAPESTRY VIEW — Archive as woven tapestry showing connections ── */}
      <section style={{ padding: '32px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(6,13,18,0.6)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <ObservationChainTapestry />
        </div>
      </section>

      {/* Search + Filters */}
      <section style={{ padding: '32px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(10,25,41,0.3)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {/* Search bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8, padding: '10px 16px', marginBottom: 20, maxWidth: 600,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="rgba(201,168,76,0.5)" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="6.5" cy="6.5" r="4.5" /><path d="M10.5 10.5l3 3" />
            </svg>
            <input
              type="text"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              placeholder="Search chains by title, theme, Name of Allah, city…"
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: '#F5F0E8', fontSize: 13, fontFamily: 'inherit',
                caretColor: '#c9a84c',
              }}
            />
            {filters.search && (
              <button onClick={() => setFilters(f => ({ ...f, search: '' }))} style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', fontSize: 16 }}>×</button>
            )}
          </div>

          {/* Filter rows */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            {/* City */}
            <div>
              <p style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 6 }}>City</p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {CITY_OPTIONS.map(c => (
                  <button key={c} onClick={() => setFilters(f => ({ ...f, city: c }))} style={{
                    fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                    background: filters.city === c ? 'rgba(45,122,71,0.12)' : 'transparent',
                    border: `1px solid ${filters.city === c ? 'rgba(45,122,71,0.4)' : 'rgba(255,255,255,0.07)'}`,
                    color: filters.city === c ? '#2d7a47' : 'rgba(212,197,169,0.35)',
                    transition: 'all 0.15s',
                  }}>{c}</button>
                ))}
              </div>
            </div>

            {/* Domain */}
            <div>
              <p style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)', marginBottom: 6 }}>Domain</p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {DOMAIN_OPTIONS.map(d => (
                  <button key={d} onClick={() => setFilters(f => ({ ...f, domain: d }))} style={{
                    fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                    background: filters.domain === d ? 'rgba(138,138,184,0.1)' : 'transparent',
                    border: `1px solid ${filters.domain === d ? 'rgba(138,138,184,0.35)' : 'rgba(255,255,255,0.07)'}`,
                    color: filters.domain === d ? '#8a8ab8' : 'rgba(212,197,169,0.35)',
                    transition: 'all 0.15s',
                  }}>{d}</button>
                ))}
              </div>
            </div>

            {/* Name of Allah */}
            <div>
              <p style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 6 }}>Name of Allah</p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {NAME_OPTIONS.map(n => (
                  <button key={n} onClick={() => setFilters(f => ({ ...f, nameOfAllah: n }))} style={{
                    fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                    background: filters.nameOfAllah === n ? 'rgba(201,168,76,0.1)' : 'transparent',
                    border: `1px solid ${filters.nameOfAllah === n ? 'rgba(201,168,76,0.35)' : 'rgba(255,255,255,0.07)'}`,
                    color: filters.nameOfAllah === n ? '#c9a84c' : 'rgba(212,197,169,0.35)',
                    transition: 'all 0.15s',
                  }}>{n}</button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div style={{ marginLeft: 'auto' }}>
              <p style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', marginBottom: 6 }}>Sort</p>
              <div style={{ display: 'flex', gap: 4 }}>
                {SORT_OPTIONS.map(s => (
                  <button key={s.id} onClick={() => setFilters(f => ({ ...f, sort: s.id as ArchiveFilters['sort'] }))} style={{
                    fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                    background: filters.sort === s.id ? 'rgba(201,168,76,0.08)' : 'transparent',
                    border: `1px solid ${filters.sort === s.id ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.07)'}`,
                    color: filters.sort === s.id ? '#c9a84c' : 'rgba(212,197,169,0.3)',
                    transition: 'all 0.15s',
                  }}>{s.label}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section style={{ padding: '32px 24px 80px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {/* Results header */}
          {!loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <span style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)' }}>
                {filtered.length} chain{filtered.length !== 1 ? 's' : ''}
                {activeFiltersCount > 0 && <span style={{ color: '#c9a84c', marginLeft: 8 }}>· {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active</span>}
              </span>
              {activeFiltersCount > 0 && (
                <button onClick={clearFilters} style={{
                  fontSize: 8, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
                  padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                  background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c',
                }}>
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            <ArchiveSkeleton />
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 24px' }}>
              <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.15 }}>◎</div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 14, fontStyle: 'italic', marginBottom: 8 }}>
                No chains found for these filters
              </p>
              <p style={{ color: 'rgba(212,197,169,0.25)', fontSize: 12, marginBottom: 20 }}>
                Try adjusting your search or clearing filters
              </p>
              <button onClick={clearFilters} style={{
                fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '8px 20px', borderRadius: 4, cursor: 'pointer',
                background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c',
              }}>
                Clear All Filters
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
              {filtered.map((chain, idx) => (
                <ChainCard key={chain.id} chain={chain} index={idx} />
              ))}
            </div>
          )}

          {/* CTA */}
          {!loading && (
            <div style={{ marginTop: 48, textAlign: 'center', paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 13, fontStyle: 'italic', marginBottom: 16 }}>
                Start your own Observation Chain — link IRL observations into a narrative sequence.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link href="/inner-reality-lens" style={{
                  fontSize: 10, fontFamily: 'monospace', letterSpacing: '.15em', textTransform: 'uppercase',
                  padding: '12px 28px', borderRadius: 4,
                  background: '#2d8a87', color: '#F5F0E8', textDecoration: 'none',
                }}>
                  Start a Chain →
                </Link>
                <Link href="/community" style={{
                  fontSize: 10, fontFamily: 'monospace', letterSpacing: '.15em', textTransform: 'uppercase',
                  padding: '12px 28px', borderRadius: 4,
                  border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', textDecoration: 'none',
                }}>
                  Community →
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
